import { create } from "zustand";

export type WizardStep = "import" | "select-songs" | "choose-writers" | "assign-writers" | "details-and-shares" | "summary-and-submit";

/** @deprecated Use WizardStep instead. Alias kept for backwards compatibility during migration. */
export type AddAlbumWizardStep = WizardStep;

type WizardState = {
  currentStep: WizardStep;
  completedSteps: Set<WizardStep>;
  canNavigateToStep: (step: WizardStep) => boolean;
  goToStep: (step: WizardStep) => void;
  nextStep: () => void;
  previousStep: () => void;
  markStepComplete: (step: WizardStep) => void;
  resetWizard: () => void;
};

const stepOrder: WizardStep[] = ["import", "select-songs", "choose-writers", "assign-writers", "details-and-shares", "summary-and-submit"];

export const useWizardStore = create<WizardState>((set, get) => ({
  currentStep: "import",
  completedSteps: new Set(),

  canNavigateToStep: (step: WizardStep) => {
    const { completedSteps } = get();
    const stepIndex = stepOrder.indexOf(step);
    const currentIndex = stepOrder.indexOf(get().currentStep);

    // Can always go backwards to completed steps
    if (stepIndex < currentIndex && completedSteps.has(step)) {
      return true;
    }

    // Can go forward only if all previous steps are completed
    for (let i = 0; i < stepIndex; i++) {
      if (!completedSteps.has(stepOrder[i])) {
        return false;
      }
    }

    return true;
  },

  goToStep: (step: WizardStep) => {
    const { canNavigateToStep } = get();
    if (canNavigateToStep(step)) {
      set({ currentStep: step });
    }
  },

  nextStep: () => {
    const { currentStep } = get();
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      set({ currentStep: stepOrder[currentIndex + 1] });
    }
  },

  previousStep: () => {
    const { currentStep } = get();
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      set({ currentStep: stepOrder[currentIndex - 1] });
    }
  },

  markStepComplete: (step: WizardStep) => {
    set(state => ({
      completedSteps: new Set([...state.completedSteps, step]),
    }));
  },

  resetWizard: () => {
    set({
      currentStep: "import",
      completedSteps: new Set(),
    });

    // Also reset other stores
    // Import and clear the assignment store
    import("./songWriterAssignmentStore").then((module) => {
      module.default.getState().clearState();
    }).catch(console.error);
  },
}));

/** @deprecated Use useWizardStore instead. Alias kept for backwards compatibility during migration. */
export const useAddAlbumWizardStore = useWizardStore;
